// File: frontend/src/views/private/Dashboard.js

import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../../hooks/useAuth';
import { hasAdminAccess } from '../../config/constants';
import apiService from '../../services/apiService';
import { 
  Upload, 
  FolderOpen, 
  FileText, 
  MessageSquare,
  Users,
  FileBarChart,
  Calendar,
  FileCode,
  TrendingUp,
  Brain,
  Database,
  ArrowRight,
  Shield
} from 'lucide-react';
import PageContainer from '../../components/layout/PageContainer';
import RecentActivities from '../../components/dashboard/RecentActivities';
import LoadingSpinner from '../../components/utility/LoadingSpinner';
import styles from './Dashboard.module.css';

const Dashboard = () => {
  const { user } = useAuth();
  const isAdmin = hasAdminAccess(user?.user_role);

  const [stats, setStats] = useState({
    policies: 0,
    claims: 0,
    conversations: 0
  });
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    loadDashboardStats();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const loadDashboardStats = async () => {
    setIsLoading(true);
    try {
      // Load files (policies)
      const filesResponse = await apiService.getUserFiles({}, user.user_id);
      const completedPolicies = (filesResponse.data?.files || []).filter(
        f => f.file_status === 'completed' && f.metadata_summary
      );

      // Load chats
      const chatsResponse = await apiService.getUserChats({}, user.user_id);
      const allChats = chatsResponse.data?.chats || [];
      const claimsChats = allChats.filter(
        c => c.agent_name === 'ai_allegoryinsurance_claims_assistant'
      );

      setStats({
        policies: completedPolicies.length,
        claims: claimsChats.length,
        conversations: allChats.filter(c => c.agent_name !== 'ai_metadata_extraction').length
      });
    } catch (err) {
      console.error('Failed to load dashboard stats:', err);
    } finally {
      setIsLoading(false);
    }
  };

  const quickActions = [
    {
      id: 'upload',
      icon: Upload,
      title: 'Upload Policy',
      description: 'Add a new insurance policy',
      to: '/dashboard/upload',
      color: '#2348ED',
      gradient: 'linear-gradient(135deg, #2348ED 0%, #1e3a8a 100%)'
    },
    {
      id: 'policies',
      icon: FolderOpen,
      title: 'View Policies',
      description: 'Access your policies',
      to: '/dashboard/policies',
      color: '#38C793',
      gradient: 'linear-gradient(135deg, #38C793 0%, #059669 100%)',
      badge: stats.policies > 0 ? stats.policies : null
    },
    {
      id: 'claims',
      icon: FileText,
      title: 'File a Claim',
      description: 'Start a new claim',
      to: '/dashboard/claims',
      color: '#F17B2C',
      gradient: 'linear-gradient(135deg, #F17B2C 0%, #ea580c 100%)',
      badge: stats.claims > 0 ? stats.claims : null
    },
    {
      id: 'chats',
      icon: MessageSquare,
      title: 'Conversations',
      description: 'View your chat history',
      to: '/dashboard/chats',
      color: '#8B5CF6',
      gradient: 'linear-gradient(135deg, #8B5CF6 0%, #7c3aed 100%)',
      badge: stats.conversations > 0 ? stats.conversations : null
    }
  ];

  const adminActions = [
    {
      id: 'ums',
      icon: Users,
      title: 'Users',
      description: 'User Management',
      to: '/dashboard/ums',
      color: '#2348ED'
    },
    {
      id: 'qms',
      icon: FileBarChart,
      title: 'Quotes',
      description: 'Quote Management',
      to: '/dashboard/qms',
      color: '#38C793'
    },
    {
      id: 'ems',
      icon: Calendar,
      title: 'Events',
      description: 'Events Management',
      to: '/dashboard/ems',
      color: '#F17B2C'
    },
    {
      id: 'tms',
      icon: FileCode,
      title: 'Templates',
      description: 'Template Management',
      to: '/dashboard/tms',
      color: '#8B5CF6'
    },
    {
      id: 'ups',
      icon: TrendingUp,
      title: 'Underwriting',
      description: 'Pricing Management',
      to: '/dashboard/ups',
      color: '#EC4899'
    },
    {
      id: 'ai',
      icon: Brain,
      title: 'AI Agents',
      description: 'Allegory Intelligence',
      to: '/dashboard/ai',
      color: '#6366F1'
    },
    {
      id: 'dms',
      icon: Database,
      title: 'Documents',
      description: 'Document Management',
      to: '/dashboard/dms',
      color: '#14B8A6'
    }
  ];

  const getGreeting = () => {
    const hour = new Date().getHours();
    if (hour < 12) return 'Good morning';
    if (hour < 18) return 'Good afternoon';
    return 'Good evening';
  };

  if (isLoading) {
    return (
      <PageContainer>
        <div className={styles.loadingContainer}>
          <LoadingSpinner />
        </div>
      </PageContainer>
    );
  }

  return (
    <PageContainer>
      <div className={styles.dashboard}>
        {/* Hero Section */}
        <section className={styles.heroSection}>
          <div className={styles.heroContent}>
            <div className={styles.heroText}>
              <h1 className={styles.heroTitle}>
                {getGreeting()}, {user?.first_name || 'User'}!
              </h1>
              <p className={styles.heroSubtitle}>
                Manage your insurance policies, file claims, and chat with AI assistants
              </p>
            </div>
            <div className={styles.heroStats}>
              <div className={styles.statCard}>
                <Shield size={20} className={styles.statIcon} />
                <div className={styles.statContent}>
                  <span className={styles.statValue}>{stats.policies}</span>
                  <span className={styles.statLabel}>Policies</span>
                </div>
              </div>
              <div className={styles.statCard}>
                <FileText size={20} className={styles.statIcon} />
                <div className={styles.statContent}>
                  <span className={styles.statValue}>{stats.claims}</span>
                  <span className={styles.statLabel}>Claims</span>
                </div>
              </div>
              <div className={styles.statCard}>
                <MessageSquare size={20} className={styles.statIcon} />
                <div className={styles.statContent}>
                  <span className={styles.statValue}>{stats.conversations}</span>
                  <span className={styles.statLabel}>Chats</span>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Quick Actions */}
        <section className={styles.actionsSection}>
          <div className={styles.sectionHeader}>
            <h2 className={styles.sectionTitle}>Quick Actions</h2>
            <p className={styles.sectionSubtitle}>Get started with common tasks</p>
          </div>
          <div className={styles.quickActionsGrid}>
            {quickActions.map((action) => {
              const IconComponent = action.icon;
              return (
                <Link 
                  key={action.id}
                  to={action.to} 
                  className={styles.quickActionCard}
                >
                  <div 
                    className={styles.quickActionIcon}
                    style={{ background: action.gradient }}
                  >
                    <IconComponent size={24} strokeWidth={2} />
                    {action.badge && (
                      <span className={styles.quickActionBadge}>{action.badge}</span>
                    )}
                  </div>
                  <div className={styles.quickActionContent}>
                    <h3 className={styles.quickActionTitle}>{action.title}</h3>
                    <p className={styles.quickActionDescription}>{action.description}</p>
                  </div>
                  <ArrowRight size={18} className={styles.quickActionArrow} />
                </Link>
              );
            })}
          </div>
        </section>

        {/* Admin Actions */}
        {isAdmin && (
          <section className={styles.actionsSection}>
            <div className={styles.sectionHeader}>
              <h2 className={styles.sectionTitle}>Admin Tools</h2>
              <p className={styles.sectionSubtitle}>Manage system resources and configurations</p>
            </div>
            <div className={styles.adminActionsGrid}>
              {adminActions.map((action) => {
                const IconComponent = action.icon;
                return (
                  <Link 
                    key={action.id}
                    to={action.to} 
                    className={styles.adminActionCard}
                  >
                    <div 
                      className={styles.adminActionIcon}
                      style={{ 
                        background: `${action.color}15`,
                        color: action.color 
                      }}
                    >
                      <IconComponent size={20} strokeWidth={2} />
                    </div>
                    <div className={styles.adminActionContent}>
                      <h3 className={styles.adminActionTitle}>{action.title}</h3>
                      <p className={styles.adminActionDescription}>{action.description}</p>
                    </div>
                  </Link>
                );
              })}
            </div>
          </section>
        )}

        {/* Recent Activities - Using your existing component */}
        <RecentActivities limit={5} showViewAll={true} />
      </div>
    </PageContainer>
  );
};

export default Dashboard;